import React, { useState, useEffect, useCallback } from "react";
import {
  MapPin,
  Plus,
  Edit,
  Trash2,
  Power,
  PowerOff,
  Search,
  X,
  Loader2,
  AlertCircle,
  Upload,
  Map,
  List,
  Download,
  Package,
  Warehouse,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { storeLocationsApi } from "../services/api";
import { useLanguage } from "../contexts/LanguageContext";
import { toast } from "react-hot-toast";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useModal } from "../hooks/useModal";
import WarehouseLocationsManagement from "./WarehouseLocationsManagement";

// Fix for default marker icons in Leaflet with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const StoreLocationsManagement = () => {
  const { translate } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedStoreForDetails, setSelectedStoreForDetails] = useState(null);
  const [showStoreDetailsModal, setShowStoreDetailsModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [selectedStoreForStock, setSelectedStoreForStock] = useState(null);
  const [stockResult, setStockResult] = useState(null);
  const [checkingStock, setCheckingStock] = useState(false);
  const [productIdInput, setProductIdInput] = useState("");

  // Determine active tab from URL
  const activeTab = location.pathname.endsWith("/map")
    ? "map"
    : location.pathname.endsWith("/warehouses")
    ? "warehouses"
    : "list";
  const [formData, setFormData] = useState({
    client_store_id: "",
    name: "",
    address: "",
    postal_code: "",
    latitude: "",
    longitude: "",
    is_active: true,
  });

  const fetchStores = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await storeLocationsApi.getAllStores(includeInactive);
      setStores(response);
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        translate("storeLocationsFetchError") ||
        "Error fetching stores";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [includeInactive, translate]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  // Redirect to /list if on base /store-locations path
  useEffect(() => {
    if (location.pathname === "/store-locations") {
      navigate("/store-locations/list", { replace: true });
    }
  }, [location.pathname, navigate]);

  const handleCreate = async () => {
    try {
      setLoading(true);
      const payload = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
      };
      await storeLocationsApi.createStore(payload);
      toast.success(
        translate("storeLocationsCreateSuccess") || "Store created successfully"
      );
      setShowCreateModal(false);
      resetForm();
      fetchStores();
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        translate("storeLocationsCreateError") ||
        "Error creating store";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setLoading(true);
      const payload = {
        name: formData.name,
        address: formData.address,
        postal_code: formData.postal_code,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        is_active: formData.is_active,
      };
      await storeLocationsApi.updateStore(
        selectedStore.client_store_id,
        payload
      );
      toast.success(
        translate("storeLocationsUpdateSuccess") || "Store updated successfully"
      );
      setShowEditModal(false);
      setSelectedStore(null);
      resetForm();
      fetchStores();
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        translate("storeLocationsUpdateError") ||
        "Error updating store";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (storeId) => {
    if (
      !window.confirm(
        translate("storeLocationsDeleteConfirm") ||
          "Are you sure you want to delete this store?"
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      await storeLocationsApi.deleteStore(storeId);
      toast.success(
        translate("storeLocationsDeleteSuccess") || "Store deleted successfully"
      );
      fetchStores();
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        translate("storeLocationsDeleteError") ||
        "Error deleting store";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (storeId) => {
    try {
      setLoading(true);
      await storeLocationsApi.toggleStoreActive(storeId);
      toast.success(
        translate("storeLocationsToggleSuccess") ||
          "Store status updated successfully"
      );
      fetchStores();
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        translate("storeLocationsToggleError") ||
        "Error updating store status";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    // Validate file type
    if (!file.name.endsWith(".json")) {
      toast.error(
        translate("storeLocationsInvalidFileType") || "File must be a JSON file"
      );
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(
        translate("storeLocationsFileTooLarge") ||
          "File size must be less than 10MB"
      );
      return;
    }

    setUploading(true);
    try {
      const result = await storeLocationsApi.importStores(file);
      if (result.success) {
        toast.success(
          translate("storeLocationsImportSuccessWithCount", {
            count: result.imported_count || 0,
          }) || `Successfully imported ${result.imported_count || 0} stores`
        );
        setShowUploadModal(false);
        fetchStores();
      } else {
        throw new Error(
          result.message ||
            translate("storeLocationsImportFailed") ||
            "Import failed"
        );
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        translate("storeLocationsImportError") ||
        "Error importing stores";
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await storeLocationsApi.exportStores();
      toast.success(
        translate("storeLocationsExportSuccess") ||
          "Stores exported successfully"
      );
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        translate("storeLocationsExportError") ||
        "Error exporting stores";
      toast.error(errorMessage);
    } finally {
      setExporting(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const resetForm = () => {
    setFormData({
      client_store_id: "",
      name: "",
      address: "",
      postal_code: "",
      latitude: "",
      longitude: "",
      is_active: true,
    });
  };

  const openEditModal = (store) => {
    setSelectedStore(store);
    setFormData({
      client_store_id: store.client_store_id,
      name: store.name,
      address: store.address,
      postal_code: store.postal_code,
      latitude: store.latitude.toString(),
      longitude: store.longitude.toString(),
      is_active: store.is_active,
    });
    setShowEditModal(true);
  };

  const openStockModal = (store) => {
    setSelectedStoreForStock(store);
    setProductIdInput("");
    setStockResult(null);
    setShowStockModal(true);
  };

  const handleCloseStockModal = () => {
    setShowStockModal(false);
    setSelectedStoreForStock(null);
    setProductIdInput("");
    setStockResult(null);
  };

  const stockModalRef = useModal(showStockModal, handleCloseStockModal);

  const handleCheckStock = async () => {
    if (!productIdInput.trim()) {
      toast.error(
        translate("storeStockProductIdRequired") || "Please enter a product ID"
      );
      return;
    }

    setCheckingStock(true);
    setStockResult(null);
    try {
      const result = await storeLocationsApi.checkStoreStock(
        selectedStoreForStock.client_store_id,
        productIdInput.trim()
      );
      setStockResult(result);
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        translate("storeStockCheckError") ||
        "Error checking stock";
      toast.error(errorMessage);
      setStockResult(null);
    } finally {
      setCheckingStock(false);
    }
  };

  const filteredStores = stores.filter((store) => {
    const query = searchQuery.toLowerCase();
    return (
      store.name.toLowerCase().includes(query) ||
      store.address.toLowerCase().includes(query) ||
      store.client_store_id.toLowerCase().includes(query) ||
      store.postal_code.toLowerCase().includes(query)
    );
  });

  return (
    <div>
      <div className="card p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-[var(--color-mint-muted)] rounded-xl">
                <MapPin className="text-[var(--color-mint)]" size={24} />
              </div>
              <div>
                <h1 className="text-2xl font-semibold" style={{ color: "var(--color-text-primary)" }}>
                  {translate("storeLocationsManagement") ||
                    "Store Locations Management"}
                </h1>
                <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
                  {translate("storeLocationsManagementDescription") ||
                    "Manage store locations"}
                </p>
              </div>
            </div>
            {/* Exportar, importar e adicionar loja sao das lojas. No separador
                dos armazens o botao de adicionar vive no proprio componente. */}
            {activeTab !== "warehouses" && (
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleExport}
                  disabled={exporting}
                  className="btn-secondary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {exporting ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <Download size={20} />
                  )}
                  <span>
                    {exporting
                      ? translate("storeLocationsExporting") ||
                        "Exporting stores..."
                      : translate("storeLocationsExportToJson") ||
                        "Export to JSON"}
                  </span>
                </button>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Upload size={20} />
                  <span>
                    {translate("storeLocationsImportFromFile") ||
                      "Import from File"}
                  </span>
                </button>
                <button
                  onClick={() => {
                    resetForm();
                    setShowCreateModal(true);
                  }}
                  className="btn-primary flex items-center space-x-2"
                >
                  <Plus size={20} />
                  <span>
                    {translate("storeLocationsAddStore") || "Add Store"}
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-[var(--color-border)]">
          <div className="flex space-x-8">
            <button
              onClick={() => navigate("/store-locations/list")}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "list"
                  ? "border-[var(--color-mint)] text-[var(--color-mint)]"
                  : "border-transparent hover:border-gray-300"
              }`}
              style={activeTab !== "list" ? { color: "var(--color-text-secondary)" } : undefined}
            >
              <div className="flex items-center space-x-2">
                <List size={18} />
                <span>{translate("storeLocationsTabList") || "List"}</span>
              </div>
            </button>
            <button
              onClick={() => navigate("/store-locations/map")}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "map"
                  ? "border-[var(--color-mint)] text-[var(--color-mint)]"
                  : "border-transparent hover:border-gray-300"
              }`}
              style={activeTab !== "map" ? { color: "var(--color-text-secondary)" } : undefined}
            >
              <div className="flex items-center space-x-2">
                <Map size={18} />
                <span>{translate("storeLocationsTabMap") || "Map"}</span>
              </div>
            </button>
            <button
              onClick={() => navigate("/store-locations/warehouses")}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "warehouses"
                  ? "border-[var(--color-mint)] text-[var(--color-mint)]"
                  : "border-transparent hover:border-gray-300"
              }`}
              style={activeTab !== "warehouses" ? { color: "var(--color-text-secondary)" } : undefined}
            >
              <div className="flex items-center space-x-2">
                <Warehouse size={18} />
                <span>
                  {translate("storeLocationsTabWarehouses") || "Warehouses"}
                </span>
              </div>
            </button>
          </div>
        </div>

        {/* Filters */}
        {activeTab === "list" && (
          <div className="mb-6 flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
                style={{ color: "var(--color-text-muted)" }}
                size={20}
              />
              <input
                type="text"
                placeholder={
                  translate("storeLocationsSearchPlaceholder") ||
                  "Search stores..."
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="field-input w-full pl-10 pr-4 py-2"
              />
            </div>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={async (e) => {
                  const newValue = e.target.checked;
                  setIncludeInactive(newValue);
                  // Force fetch with new value
                  setLoading(true);
                  setError(null);
                  try {
                    const response = await storeLocationsApi.getAllStores(
                      newValue
                    );
                    setStores(response);
                  } catch (err) {
                    const errorMessage =
                      err.response?.data?.detail ||
                      err.message ||
                      translate("storeLocationsFetchError") ||
                      "Error fetching stores";
                    setError(errorMessage);
                    toast.error(errorMessage);
                  } finally {
                    setLoading(false);
                  }
                }}
                className="w-4 h-4 text-[var(--color-mint)] rounded focus:ring-[var(--color-mint)]"
              />
              <span className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                {translate("storeLocationsIncludeInactive") ||
                  "Include inactive"}
              </span>
            </label>
          </div>
        )}

        {/* Error Message. O erro e da lista de lojas, que continua a ser
            carregada em todos os separadores; no dos armazens falaria de lojas
            por cima de uma lista de armazens que esta a funcionar. */}
        {activeTab !== "warehouses" && error && (
          <div className="mb-6 p-4 rounded-xl flex items-center space-x-3" style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
            <AlertCircle className="flex-shrink-0" size={20} style={{ color: "var(--color-error)" }} />
            <p style={{ color: "var(--color-error)" }}>{error}</p>
          </div>
        )}

        {/* Warehouses View */}
        {activeTab === "warehouses" && <WarehouseLocationsManagement />}

        {/* Map View */}
        {activeTab === "map" && (
          <StoreMapView
            stores={filteredStores}
            loading={loading}
            onStoreClick={(store) => {
              setSelectedStoreForDetails(store);
              setShowStoreDetailsModal(true);
            }}
          />
        )}

        {/* List View */}
        {activeTab === "list" && (
          <>
            {/* Loading State - Skeleton */}
            {loading && (
              <div className="overflow-x-auto animate-pulse">
                <div className="bg-[var(--color-surface)] rounded-xl border border-[var(--color-border)] overflow-hidden">
                  {/* Table Header Skeleton */}
                  <div className="px-4 py-3 border-b border-[var(--color-border)]" style={{ backgroundColor: "var(--color-surface-raised)" }}>
                    <div className="grid grid-cols-6 gap-4">
                      <div className="h-5 w-20 rounded bg-[var(--color-skeleton)]" />
                      <div className="h-5 w-24 rounded bg-[var(--color-skeleton)]" />
                      <div className="h-5 w-32 rounded bg-[var(--color-skeleton)]" />
                      <div className="h-5 w-24 rounded bg-[var(--color-skeleton)]" />
                      <div className="h-5 w-16 rounded bg-[var(--color-skeleton)]" />
                      <div className="h-5 w-20 rounded bg-[var(--color-skeleton)] ml-auto" />
                    </div>
                  </div>
                  {/* Table Rows Skeleton */}
                  <div className="divide-y divide-[var(--color-border)]">
                    {Array.from({ length: 8 }).map((_, index) => (
                      <div key={index} className="px-4 py-3">
                        <div className="grid grid-cols-6 gap-4 items-center">
                          <div className="h-4 w-20 rounded bg-[var(--color-skeleton-light)]" />
                          <div className="h-4 w-32 rounded bg-[var(--color-skeleton-light)]" />
                          <div className="h-4 w-40 rounded bg-[var(--color-skeleton-light)]" />
                          <div className="h-4 w-24 rounded bg-[var(--color-skeleton-light)]" />
                          <div className="h-6 w-16 rounded-full bg-[var(--color-skeleton-light)]" />
                          <div className="flex items-center justify-end space-x-2">
                            <div className="w-8 h-8 rounded bg-[var(--color-skeleton-light)]" />
                            <div className="w-8 h-8 rounded bg-[var(--color-skeleton-light)]" />
                            <div className="w-8 h-8 rounded bg-[var(--color-skeleton-light)]" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Stores Table */}
            {!loading && stores.length > 0 && filteredStores.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr style={{ backgroundColor: "var(--color-surface-raised)" }} className="border-b border-[var(--color-border)]">
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--color-text-muted)" }}>
                        {translate("storeLocationsTableId") || "ID"}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--color-text-muted)" }}>
                        {translate("storeLocationsTableName") || "Name"}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--color-text-muted)" }}>
                        {translate("storeLocationsTableAddress") || "Address"}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--color-text-muted)" }}>
                        {translate("storeLocationsTablePostalCode") ||
                          "Postal Code"}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--color-text-muted)" }}>
                        {translate("storeLocationsTableStatus") || "Status"}
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--color-text-muted)" }}>
                        {translate("storeLocationsTableActions") || "Actions"}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStores.map((store) => (
                      <tr
                        key={store.client_store_id}
                        className="border-b border-[var(--color-border)] hover:bg-[var(--color-surface-raised)]"
                      >
                        <td className="px-4 py-3 text-sm" style={{ color: "var(--color-text-primary)" }}>
                          {store.client_store_id}
                        </td>
                        <td className="px-4 py-3 text-sm" style={{ color: "var(--color-text-primary)" }}>
                          {store.name}
                        </td>
                        <td className="px-4 py-3 text-sm" style={{ color: "var(--color-text-secondary)" }}>
                          {store.address}
                        </td>
                        <td className="px-4 py-3 text-sm" style={{ color: "var(--color-text-secondary)" }}>
                          {store.postal_code}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              store.is_active
                                ? "badge-active"
                                : "badge-inactive"
                            }`}
                          >
                            {store.is_active
                              ? translate("storeLocationsActive") || "Active"
                              : translate("storeLocationsInactive") ||
                                "Inactive"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => openStockModal(store)}
                              className="p-2 rounded transition-colors hover:bg-[var(--color-mint-muted)]"
                              style={{ color: "var(--color-text-muted)" }}
                              onMouseEnter={(e) => { e.currentTarget.style.color = "var(--color-mint)"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.color = "var(--color-text-muted)"; }}
                              title={
                                translate("storeStockCheckStock") ||
                                "Check Stock"
                              }
                            >
                              <Package size={18} />
                            </button>
                            <button
                              onClick={() =>
                                handleToggleActive(store.client_store_id)
                              }
                              className="p-2 rounded transition-colors hover:bg-[var(--color-mint-muted)]"
                              style={{ color: "var(--color-text-muted)" }}
                              onMouseEnter={(e) => { e.currentTarget.style.color = "var(--color-mint)"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.color = "var(--color-text-muted)"; }}
                              title={
                                store.is_active
                                  ? translate("storeLocationsDeactivate") ||
                                    "Deactivate"
                                  : translate("storeLocationsActivate") ||
                                    "Activate"
                              }
                            >
                              {store.is_active ? (
                                <PowerOff size={18} />
                              ) : (
                                <Power size={18} />
                              )}
                            </button>
                            <button
                              onClick={() => openEditModal(store)}
                              className="p-2 rounded transition-colors hover:bg-[var(--color-mint-muted)]"
                              style={{ color: "var(--color-text-muted)" }}
                              onMouseEnter={(e) => { e.currentTarget.style.color = "var(--color-mint)"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.color = "var(--color-text-muted)"; }}
                              title={translate("storeLocationsEdit") || "Edit"}
                            >
                              <Edit size={18} />
                            </button>
                            <button
                              onClick={() =>
                                handleDelete(store.client_store_id)
                              }
                              className="p-2 rounded transition-colors"
                              style={{ color: "var(--color-text-muted)" }}
                              onMouseEnter={(e) => { e.currentTarget.style.color = "var(--color-error)"; e.currentTarget.style.backgroundColor = "rgba(239,68,68,0.08)"; }}
                              onMouseLeave={(e) => { e.currentTarget.style.color = "var(--color-text-muted)"; e.currentTarget.style.backgroundColor = "transparent"; }}
                              title={
                                translate("storeLocationsDelete") || "Delete"
                              }
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Empty State */}
            {!loading && stores.length === 0 && (
              <div className="text-center py-12">
                <MapPin className="mx-auto mb-4" size={48} style={{ color: "var(--color-text-muted)" }} />
                <p style={{ color: "var(--color-text-secondary)" }}>
                  {translate("storeLocationsNoStores") ||
                    "No stores found. Add your first store to get started."}
                </p>
              </div>
            )}

            {/* No Results from Search */}
            {!loading && stores.length > 0 && filteredStores.length === 0 && (
              <div className="text-center py-12">
                <Search className="mx-auto mb-4" size={48} style={{ color: "var(--color-text-muted)" }} />
                <p style={{ color: "var(--color-text-secondary)" }}>
                  {translate("storeLocationsNoResults") ||
                    "No stores match your search criteria."}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <StoreLocationModal
          title={translate("storeLocationsCreateStore") || "Create Store"}
          formData={formData}
          setFormData={setFormData}
          onSave={handleCreate}
          onCancel={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          loading={loading}
          isCreate={true}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedStore && (
        <StoreLocationModal
          title={translate("storeLocationsEditStore") || "Edit Store"}
          formData={formData}
          setFormData={setFormData}
          onSave={handleUpdate}
          onCancel={() => {
            setShowEditModal(false);
            setSelectedStore(null);
            resetForm();
          }}
          loading={loading}
          isCreate={false}
        />
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          onFileSelect={handleFileSelect}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onCancel={() => setShowUploadModal(false)}
          uploading={uploading}
          isDragOver={isDragOver}
        />
      )}

      {/* Store Details Modal */}
      {showStoreDetailsModal && selectedStoreForDetails && (
        <StoreDetailsModal
          store={selectedStoreForDetails}
          onClose={() => {
            setShowStoreDetailsModal(false);
            setSelectedStoreForDetails(null);
          }}
          onEdit={() => {
            setShowStoreDetailsModal(false);
            openEditModal(selectedStoreForDetails);
          }}
          onDelete={() => {
            setShowStoreDetailsModal(false);
            handleDelete(selectedStoreForDetails.client_store_id);
          }}
          onToggleActive={() => {
            handleToggleActive(selectedStoreForDetails.client_store_id);
          }}
        />
      )}

      {/* Store Stock Modal */}
      {showStockModal && selectedStoreForStock && (
        <StoreStockModal
          store={selectedStoreForStock}
          productId={productIdInput}
          setProductId={setProductIdInput}
          stockResult={stockResult}
          checkingStock={checkingStock}
          onCheck={handleCheckStock}
          onClose={handleCloseStockModal}
          modalRef={stockModalRef}
        />
      )}
    </div>
  );
};

const StoreMapView = ({ stores, loading, onStoreClick }) => {
  const { translate } = useLanguage();

  if (loading) {
    return (
      <div className="h-[600px] bg-[var(--color-surface-raised)] rounded-xl flex items-center justify-center">
        <div style={{ width: 48, height: 48, border: '3px solid var(--color-mint)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  if (stores.length === 0) {
    return (
      <div className="h-[600px] bg-[var(--color-surface-raised)] rounded-xl flex items-center justify-center">
        <div className="text-center">
          <MapPin className="mx-auto mb-4" size={48} style={{ color: "var(--color-text-muted)" }} />
          <p style={{ color: "var(--color-text-secondary)" }}>
            {translate("storeLocationsNoStores") ||
              "No stores found to display on map."}
          </p>
        </div>
      </div>
    );
  }

  // Calculate center of all stores
  const centerLat =
    stores.reduce((sum, store) => sum + store.latitude, 0) / stores.length;
  const centerLng =
    stores.reduce((sum, store) => sum + store.longitude, 0) / stores.length;

  // Calculate zoom level based on store distribution
  const calculateZoom = () => {
    if (stores.length === 0) return 8;
    if (stores.length === 1) return 13;

    const lats = stores.map((s) => s.latitude);
    const lngs = stores.map((s) => s.longitude);
    const latDiff = Math.max(...lats) - Math.min(...lats);
    const lngDiff = Math.max(...lngs) - Math.min(...lngs);
    const maxDiff = Math.max(latDiff, lngDiff);

    if (maxDiff < 0.01) return 13;
    if (maxDiff < 0.1) return 11;
    if (maxDiff < 0.5) return 9;
    return 8;
  };

  return (
    <div className="relative">
      <div className="h-[600px] w-full rounded-xl overflow-hidden border border-[var(--color-border)] relative z-0">
        <MapContainer
          center={[centerLat, centerLng]}
          zoom={calculateZoom()}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {stores.map((store) => (
            <Marker
              key={store.client_store_id}
              position={[store.latitude, store.longitude]}
            >
              <Popup>
                <div className="p-2">
                  <h3 className="font-semibold text-sm mb-1" style={{ color: "var(--color-text-primary)" }}>
                    {store.name}
                  </h3>
                  <p className="text-xs mb-1" style={{ color: "var(--color-text-secondary)" }}>{store.address}</p>
                  <p className="text-xs mb-2" style={{ color: "var(--color-text-secondary)" }}>
                    {store.postal_code}
                  </p>
                  <button
                    onClick={() => onStoreClick(store)}
                    className="text-xs font-medium"
                    style={{ color: "var(--color-mint)" }}
                  >
                    {translate("storeLocationsViewDetails") || "View Details"}
                  </button>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
        {/* Overlay with store markers info */}
        <div className="absolute top-4 right-4 rounded-lg shadow-lg p-3 max-w-xs z-[1000]" style={{ backgroundColor: "var(--color-surface)" }}>
          <p className="text-xs font-semibold mb-2" style={{ color: "var(--color-text-muted)" }}>
            {stores.length}{" "}
            {stores.length === 1
              ? translate("storeLocationsStoreSingular") || "store"
              : translate("storeLocationsStorePlural") || "stores"}{" "}
            {translate("storeLocationsShown") || "shown"}
          </p>
          <p className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
            {translate("storeLocationsMapOverlayHint") ||
              "Click on markers to view details"}
          </p>
        </div>
      </div>
    </div>
  );
};

const StoreDetailsModal = ({
  store,
  onClose,
  onEdit,
  onDelete,
  onToggleActive,
}) => {
  const { translate } = useLanguage();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" style={{ backgroundColor: "var(--color-surface)" }}>
        <div className="p-6 border-b border-[var(--color-border)]">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold" style={{ color: "var(--color-text-primary)" }}>
              {translate("storeLocationsStoreDetails") || "Store Details"}
            </h2>
            <button
              onClick={onClose}
              style={{ color: "var(--color-text-muted)" }}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-secondary)" }}>
              {translate("storeLocationsFormId") || "Store ID"}
            </label>
            <p style={{ color: "var(--color-text-primary)" }}>{store.client_store_id}</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-secondary)" }}>
              {translate("storeLocationsFormName") || "Name"}
            </label>
            <p style={{ color: "var(--color-text-primary)" }}>{store.name}</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-secondary)" }}>
              {translate("storeLocationsFormAddress") || "Address"}
            </label>
            <p style={{ color: "var(--color-text-primary)" }}>{store.address}</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-secondary)" }}>
              {translate("storeLocationsFormPostalCode") || "Postal Code"}
            </label>
            <p style={{ color: "var(--color-text-primary)" }}>{store.postal_code}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-secondary)" }}>
                {translate("storeLocationsFormLatitude") || "Latitude"}
              </label>
              <p style={{ color: "var(--color-text-primary)" }}>{store.latitude}</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-secondary)" }}>
                {translate("storeLocationsFormLongitude") || "Longitude"}
              </label>
              <p style={{ color: "var(--color-text-primary)" }}>{store.longitude}</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-secondary)" }}>
              {translate("storeLocationsTableStatus") || "Status"}
            </label>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                store.is_active
                  ? "badge-active"
                  : "badge-inactive"
              }`}
            >
              {store.is_active
                ? translate("storeLocationsActive") || "Active"
                : translate("storeLocationsInactive") || "Inactive"}
            </span>
          </div>

          {store.created_at && (
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-secondary)" }}>
                {translate("storeLocationsCreatedAt") || "Created At"}
              </label>
              <p style={{ color: "var(--color-text-primary)" }}>
                {new Date(store.created_at).toLocaleString()}
              </p>
            </div>
          )}

          {store.updated_at && (
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-secondary)" }}>
                {translate("storeLocationsUpdatedAt") || "Updated At"}
              </label>
              <p style={{ color: "var(--color-text-primary)" }}>
                {new Date(store.updated_at).toLocaleString()}
              </p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-[var(--color-border)] flex items-center justify-end space-x-3">
          <button
            onClick={onToggleActive}
            className="btn-secondary flex items-center space-x-2"
          >
            {store.is_active ? (
              <>
                <PowerOff size={16} />
                <span>
                  {translate("storeLocationsDeactivate") || "Deactivate"}
                </span>
              </>
            ) : (
              <>
                <Power size={16} />
                <span>{translate("storeLocationsActivate") || "Activate"}</span>
              </>
            )}
          </button>
          <button
            onClick={onEdit}
            className="btn-secondary flex items-center space-x-2"
          >
            <Edit size={16} />
            <span>{translate("storeLocationsEdit") || "Edit"}</span>
          </button>
          <button
            onClick={onDelete}
            className="btn-destructive flex items-center space-x-2"
          >
            <Trash2 size={16} />
            <span>{translate("storeLocationsDelete") || "Delete"}</span>
          </button>
          <button
            onClick={onClose}
            className="btn-secondary"
          >
            {translate("close") || "Close"}
          </button>
        </div>
      </div>
    </div>
  );
};

const UploadModal = ({
  onFileSelect,
  onDrop,
  onDragOver,
  onDragLeave,
  onCancel,
  uploading,
  isDragOver,
}) => {
  const { translate } = useLanguage();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="rounded-xl shadow-xl max-w-2xl w-full mx-4" style={{ backgroundColor: "var(--color-surface)" }}>
        <div className="p-6 border-b border-[var(--color-border)]">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold" style={{ color: "var(--color-text-primary)" }}>
              {translate("storeLocationsImportFromFile") ||
                "Import Stores from File"}
            </h2>
            <button
              onClick={onCancel}
              disabled={uploading}
              className="disabled:opacity-50"
              style={{ color: "var(--color-text-muted)" }}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div
            className={`border-2 border-dashed rounded-xl p-12 text-center transition-all ${
              isDragOver
                ? "border-[var(--color-mint)] bg-[var(--color-mint-muted)]"
                : "border-[var(--color-border)] hover:border-[var(--color-mint)]"
            } ${
              uploading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            }`}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
          >
            {uploading ? (
              <div className="flex flex-col items-center">
                <div className="mb-4 mx-auto" style={{ width: 48, height: 48, border: '3px solid var(--color-mint)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <p className="text-lg" style={{ color: "var(--color-text-primary)" }}>
                  {translate("storeLocationsUploading") ||
                    "Uploading and importing stores..."}
                </p>
              </div>
            ) : (
              <>
                <Upload className="mx-auto mb-4" size={48} style={{ color: "var(--color-text-muted)" }} />
                <p className="text-lg font-semibold mb-2" style={{ color: "var(--color-text-primary)" }}>
                  {translate("storeLocationsDragDrop") ||
                    "Drag and drop a JSON file here, or click to select"}
                </p>
                <p className="text-sm mb-4" style={{ color: "var(--color-text-secondary)" }}>
                  {translate("storeLocationsFileFormat") ||
                    "File format: JSON array with store objects. All fields (id, nome, morada, cod_postal, latitude, longitude) are required."}
                </p>
                <label className="inline-block">
                  <input
                    type="file"
                    accept=".json"
                    onChange={onFileSelect}
                    className="hidden"
                    disabled={uploading}
                  />
                  <span className="btn-primary cursor-pointer">
                    {translate("storeLocationsSelectFile") ||
                      "Select JSON File"}
                  </span>
                </label>
              </>
            )}
          </div>

          {/* Instructions */}
          <div className="mt-6 bg-[var(--color-surface-raised)] rounded-xl p-6 border border-[var(--color-border)]">
            <h3 className="text-lg font-semibold mb-4" style={{ color: "var(--color-text-primary)" }}>
              {translate("storeLocationsInstructions") || "Instructions"}
            </h3>
            <div className="space-y-3 text-sm" style={{ color: "var(--color-text-primary)" }}>
              <p>
                {translate("storeLocationsInstructions1") ||
                  "1. Prepare a JSON file with an array of store objects"}
              </p>
              <p>
                {translate("storeLocationsInstructions2") ||
                  "2. Each store object must contain the following fields:"}
              </p>
              <ul className="list-disc list-inside ml-4 space-y-1" style={{ color: "var(--color-text-secondary)" }}>
                <li>
                  <code className="bg-[var(--color-border)] px-1 rounded">id</code> -{" "}
                  {translate("storeLocationsFieldId") || "Store ID (required)"}
                </li>
                <li>
                  <code className="bg-[var(--color-surface-raised)] border border-[var(--color-border)] px-1 rounded">nome</code> -{" "}
                  {translate("storeLocationsFieldNome") ||
                    "Store name (required)"}
                </li>
                <li>
                  <code className="bg-[var(--color-surface-raised)] border border-[var(--color-border)] px-1 rounded">morada</code> -{" "}
                  {translate("storeLocationsFieldMorada") ||
                    "Address (required)"}
                </li>
                <li>
                  <code className="bg-[var(--color-surface-raised)] border border-[var(--color-border)] px-1 rounded">cod_postal</code> -{" "}
                  {translate("storeLocationsFieldCodPostal") ||
                    "Postal code (required)"}
                </li>
                <li>
                  <code className="bg-[var(--color-surface-raised)] border border-[var(--color-border)] px-1 rounded">latitude</code> -{" "}
                  {translate("storeLocationsFieldLatitude") ||
                    "Latitude (required)"}
                </li>
                <li>
                  <code className="bg-[var(--color-surface-raised)] border border-[var(--color-border)] px-1 rounded">longitude</code> -{" "}
                  {translate("storeLocationsFieldLongitude") ||
                    "Longitude (required)"}
                </li>
              </ul>
              <p className="mt-4">
                {translate("storeLocationsInstructions3") ||
                  "3. Upload the file using the area above"}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-[var(--color-border)] flex items-center justify-end">
          <button
            onClick={onCancel}
            disabled={uploading}
            className="btn-secondary disabled:opacity-50"
          >
            {translate("cancel") || "Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
};

const StoreLocationModal = ({
  title,
  formData,
  setFormData,
  onSave,
  onCancel,
  loading,
  isCreate,
}) => {
  const { translate } = useLanguage();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" style={{ backgroundColor: "var(--color-surface)" }}>
        <div className="p-6 border-b border-[var(--color-border)]">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold" style={{ color: "var(--color-text-primary)" }}>{title}</h2>
            <button
              onClick={onCancel}
              style={{ color: "var(--color-text-muted)" }}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {isCreate && (
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-secondary)" }}>
                {translate("storeLocationsFormId") || "Store ID"} *
              </label>
              <input
                type="text"
                value={formData.client_store_id}
                onChange={(e) =>
                  setFormData({ ...formData, client_store_id: e.target.value })
                }
                className="field-input w-full"
                required
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-secondary)" }}>
              {translate("storeLocationsFormName") || "Name"} *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="field-input w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-secondary)" }}>
              {translate("storeLocationsFormAddress") || "Address"} *
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) =>
                setFormData({ ...formData, address: e.target.value })
              }
              className="field-input w-full"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-secondary)" }}>
              {translate("storeLocationsFormPostalCode") || "Postal Code"} *
            </label>
            <input
              type="text"
              value={formData.postal_code}
              onChange={(e) =>
                setFormData({ ...formData, postal_code: e.target.value })
              }
              className="field-input w-full"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-secondary)" }}>
                {translate("storeLocationsFormLatitude") || "Latitude"} *
              </label>
              <input
                type="number"
                step="any"
                min="-90"
                max="90"
                value={formData.latitude}
                onChange={(e) =>
                  setFormData({ ...formData, latitude: e.target.value })
                }
                className="field-input w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-secondary)" }}>
                {translate("storeLocationsFormLongitude") || "Longitude"} *
              </label>
              <input
                type="number"
                step="any"
                min="-180"
                max="180"
                value={formData.longitude}
                onChange={(e) =>
                  setFormData({ ...formData, longitude: e.target.value })
                }
                className="field-input w-full"
                required
              />
            </div>
          </div>

          <div>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData({ ...formData, is_active: e.target.checked })
                }
                className="w-4 h-4 text-[var(--color-mint)] rounded focus:ring-[var(--color-mint)]"
              />
              <span className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                {translate("storeLocationsFormActive") || "Active"}
              </span>
            </label>
          </div>
        </div>

        <div className="p-6 border-t border-[var(--color-border)] flex items-center justify-end space-x-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="btn-secondary disabled:opacity-50"
          >
            {translate("cancel") || "Cancel"}
          </button>
          <button
            onClick={onSave}
            disabled={loading}
            className="btn-primary disabled:opacity-50 flex items-center space-x-2"
          >
            {loading && <Loader2 className="animate-spin" size={16} />}
            <span>{translate("save") || "Save"}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const StoreStockModal = ({
  store,
  productId,
  setProductId,
  stockResult,
  checkingStock,
  onCheck,
  onClose,
  modalRef,
}) => {
  const { translate } = useLanguage();

  const getStockLevelText = (stockLevel) => {
    switch (stockLevel) {
      case "no_stock":
        return translate("storeStockNoStockStatus") || "Sem Stock";
      case "last_units":
        return translate("storeStockLastUnits") || "Últimas unidades";
      case "low_stock":
        return translate("storeStockLowStock") || "Poucas unidades";
      case "many":
        return translate("storeStockManyUnits") || "Várias unidades";
      default:
        return stockLevel;
    }
  };

  const getStockLevelColor = (stockLevel) => {
    switch (stockLevel) {
      case "no_stock":
        return "badge-inactive";
      case "last_units":
        return "bg-red-100 text-red-800";
      case "low_stock":
        return "bg-yellow-100 text-yellow-800";
      case "many":
        return "badge-active";
      default:
        return "badge-inactive";
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: "var(--color-surface)" }}
      >
        <div className="flex items-center justify-between p-6 border-b border-[var(--color-border)]">
          <h2 className="text-xl font-semibold" style={{ color: "var(--color-text-primary)" }}>
            {translate("storeStockModalTitle") || "Consultar Stock"}
          </h2>
          <button
            onClick={onClose}
            className="transition-colors"
            style={{ color: "var(--color-text-muted)" }}
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-4">
            <p className="text-lg font-medium" style={{ color: "var(--color-text-primary)" }}>{store.name}</p>
            <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>{store.address}</p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-text-secondary)" }}>
              {translate("storeStockProductIdLabel") || "ID do Produto"} *
            </label>
            <input
              type="text"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !checkingStock) {
                  onCheck();
                }
              }}
              className="field-input w-full"
              placeholder={
                translate("storeStockProductIdPlaceholder") ||
                "Digite o ID do produto"
              }
              disabled={checkingStock}
            />
          </div>

          <button
            onClick={onCheck}
            disabled={checkingStock || !productId.trim()}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {checkingStock ? (
              <>
                <Loader2 className="animate-spin mr-2" size={18} />
                {translate("storeStockChecking") || "A consultar..."}
              </>
            ) : (
              translate("storeStockCheckButton") || "Consultar"
            )}
          </button>

          {stockResult && (
            <div className="mt-6 p-4 bg-[var(--color-surface-raised)] rounded-xl border border-[var(--color-border)]">
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: "var(--color-text-muted)" }}>
                {translate("storeStockResult") || "Resultado"}
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                    {translate("storeStockProductId") || "Produto"}
                  </span>
                  <span className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                    {stockResult.product_id}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                    {translate("storeStockQuantity") || "Quantidade"}
                  </span>
                  <span className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                    {stockResult.quantity}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                    {translate("storeStockStatus") || "Estado"}
                  </span>
                  <span
                    className={`text-sm font-medium px-2 py-1 rounded ${getStockLevelColor(
                      stockResult.stock_level
                    )}`}
                  >
                    {getStockLevelText(stockResult.stock_level)}
                  </span>
                </div>
                {stockResult.stock_level === "no_stock" && (
                  <div className="mt-2 p-2 bg-[var(--color-surface-raised)] border border-[var(--color-border)] rounded-xl text-sm" style={{ color: "var(--color-text-primary)" }}>
                    {translate("storeStockNoStock") || "Sem stock disponível"}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoreLocationsManagement;
